import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;
/**
 * Delay before swapping the drawer panel. Moving the pointer diagonally from a
 * section towards the panel crosses the sections below it; without this delay
 * the panel would flicker through each one on the way.
 */
const PANEL_SELECT_DELAY = 120;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    window.addEventListener('resize', this.#handleResize, {
      signal: this.#abortController.signal,
    });

    // In the editor, selecting a block sitting in a closed panel would show nothing.
    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.#handleBlockSelect, {
        signal: this.#abortController.signal,
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  #submenuHeightTokens = new WeakMap();
  #resizeToken = 0;
  #imagesPreloaded = false;

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    // Menu images are only fetched on the first open. Fetching them on page `load`
    // would cost one request per image on every page of the store.
    if (!this.#imagesPreloaded) {
      this.#imagesPreloaded = true;
      this.#preloadImages();
    }

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    // If the item was just closed, it may still be flagged as animating.
    // That state forces submenu content opacity to 0 in CSS, which looks like an "empty/white" mega menu.
    item?.removeAttribute('data-animating');

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    // The drawer fills whatever is left below the header. `--header-group-height`
    // is not a reliable stand-in for that distance — it excludes the header itself
    // unless the header is transparent — so read the panel's own offset instead.
    // One read per open, no rAF loop and no re-measure of the content.
    if (isDrawerItem(item)) {
      if (submenu) this.#updateDrawerHeight(submenu);
      this.style.setProperty('--submenu-height', '0px');
      this.style.setProperty('--submenu-opacity', '1');
      return;
    }

    if (submenu) {
      this.#scheduleSubmenuHeightUpdate(submenu, item);
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Reveal the drawer panel holding the block selected in the theme editor.
   * @param {Event} event
   */
  #handleBlockSelect = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !this.contains(target)) return;

    const subpanel = target.closest('.menu-subpanel');
    if (!subpanel) return;

    const section = subpanel.querySelector('.mega-drawer__section-link');
    if (section instanceof HTMLElement) {
      this.#debouncedSelectPanel.cancel();
      this.#selectPanelHandler(/** @type {any} */ ({ target: section }));
    }

    // Open the drawer too, otherwise the selected block stays out of sight.
    const item = subpanel.closest('.menu-list__list-item--drawer');
    if (item instanceof HTMLElement) {
      this.#debouncedDeactivate.cancel();
      this.#activateHandler(/** @type {any} */ ({ target: item }));
    }
  };

  /**
   * Reveal the panel of the hovered or focused drawer section.
   * @param {PointerEvent | FocusEvent} event
   */
  selectPanel = (event) => {
    // The section title carries its own `on:focus` and `on:pointerenter`, which shadow
    // the ones on the menu entry: the delegation only ever fires the deepest handler.
    // Nothing else would cancel the close scheduled when the focus left the previous
    // element, and the drawer would shut itself down while the visitor is inside it.
    this.#debouncedDeactivate.cancel();
    this.#debouncedSelectPanel.cancel();
    this.#debouncedSelectPanel(event);
  };

  /**
   * @param {PointerEvent | FocusEvent} event
   */
  #selectPanelHandler = (event) => {
    const trigger = event.target;
    if (!(trigger instanceof HTMLElement)) return;
    if (trigger.getAttribute('aria-expanded') === 'true') return;

    const drawer = trigger.closest('.mega-drawer');
    const panelId = trigger.getAttribute('aria-controls');
    if (!drawer || !panelId) return;

    const subpanel = trigger.closest('.menu-subpanel');
    if (!subpanel || !drawer.querySelector(`#${CSS.escape(panelId)}`)) return;

    for (const section of drawer.querySelectorAll('.mega-drawer__section-link[aria-expanded="true"]')) {
      section.setAttribute('aria-expanded', 'false');
    }
    for (const other of drawer.querySelectorAll('.menu-subpanel[data-active]')) {
      other.removeAttribute('data-active');
    }

    trigger.setAttribute('aria-expanded', 'true');
    subpanel.setAttribute('data-active', '');
  };

  #debouncedSelectPanel = debounce(this.#selectPanelHandler, PANEL_SELECT_DELAY);

  /**
   * Size the drawer down to the bottom of the viewport.
   * @param {HTMLElement} submenu
   */
  #updateDrawerHeight = (submenu) => {
    const top = submenu.getBoundingClientRect().top;
    this.style.setProperty('--mega-drawer-height', `${Math.max(0, window.innerHeight - top)}px`);
  };

  /**
   * Put the drawer back on its default section, so that reopening the menu does
   * not show whichever panel happened to be hovered last.
   * @param {HTMLElement} item
   */
  #resetDrawer = (item) => {
    const drawer = item.closest('.menu-list__list-item--drawer')?.querySelector('.mega-drawer');
    if (!drawer) return;

    for (const section of drawer.querySelectorAll('.mega-drawer__section-link[aria-expanded="true"]')) {
      section.setAttribute('aria-expanded', 'false');
    }
    // Clearing the marker hands the default subpanel back to the CSS `:first-child` rule.
    for (const subpanel of drawer.querySelectorAll('.menu-subpanel[data-active]')) {
      subpanel.removeAttribute('data-active');
    }
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();
    this.#debouncedSelectPanel.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      const related = /** @type {any} */ (event).relatedTarget;
      const leavingMenu = !(related instanceof Node) || !this.contains(related);
      // Leaving the menu for the page closes it right away, rather than relying on a
      // debounce that throttling may delay or a re-entry may cancel. The drawer is the
      // exception: everything but its columns is inert, so the pointer lands on the page
      // as soon as it crosses the dimmed area — from where it can still come back into a
      // column. That case keeps the delay, and the columns re-arm the entry on entering.
      if (leavingMenu && !isDrawerItem(item)) {
        this.#deactivate(item);
        return;
      }

      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    if (isDrawerItem(item)) this.#resetDrawer(item);

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  #handleResize = debounce(() => {
    this.#resizeToken += 1;
    const item = this.#state.activeItem;
    if (!item) return;
    if (isDrawerItem(item)) {
      const drawerSubmenu = findSubmenu(item);
      if (drawerSubmenu) this.#updateDrawerHeight(drawerSubmenu);
      return;
    }

    let submenu = findSubmenu(item);
    if (!submenu) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      this.#scheduleSubmenuHeightUpdate(submenu, item, true);
    }
  }, 100);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  /**
   * Schedule a single submenu height measurement per open.
   * @param {HTMLElement} submenu
   * @param {HTMLElement} item
   * @param {boolean} force
   */
  #scheduleSubmenuHeightUpdate = (submenu, item, force = false) => {
    const currentToken = this.#resizeToken;
    if (!force && this.#submenuHeightTokens.get(submenu) === currentToken) return;

    requestAnimationFrame(() => {
      if (this.#state.activeItem !== item) return;
      this.#updateSubmenuHeight(submenu);
      this.#submenuHeightTokens.set(submenu, currentToken);
    });
  };

  /**
   * Re-measure the submenu once its content has been laid out.
   * @param {HTMLElement} submenu
   */
  #updateSubmenuHeight = (submenu) => {
    if (!(submenu instanceof HTMLElement)) return;

    this.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Whether a menu item opens its panel as a full height drawer.
 * @param {Element | null | undefined} element
 * @returns {boolean}
 */
function isDrawerItem(element) {
  return element?.closest('.menu-list__list-item--drawer') != null;
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
